import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { X, Save as SaveIcon, Trash2, Sparkles, Upload, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { apiClient } from '@/services/api';
import { uploadMedia } from '@/services/storage';
import { ImageSearchModal } from './ImageSearchModal';

interface Props {
  product: any | null;       // null = criar novo, objeto = editar
  onClose: () => void;
}

const COMMON_UNIDADES = ['UN', 'KG', 'G', 'L', 'ML', 'PC', 'CX', 'DZ'];

export function ProductFormModal({ product, onClose }: Props) {
  const qc = useQueryClient();
  const isEdit = !!product;

  const { data: managedCategories = [] } = useQuery({
    queryKey: ['product-categories-managed'],
    queryFn: () => apiClient.getProductCategoriesManaged().then(r => r.data as { nome: string }[]),
  });

  const { data: managedDepartments = [] } = useQuery({
    queryKey: ['product-departments-managed'],
    queryFn: () => apiClient.getProductDepartmentsManaged().then(r => r.data as { nome: string }[]),
  });

  const [imageSearchOpen, setImageSearchOpen] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const [form, setForm] = useState({
    codigo: '',
    descricao: '',
    descricao_curta: '',
    categoria: '',
    departamento: '',
    marca: '',
    preco_reais: '',
    preco_promo_reais: '',
    promo_inicio: '',
    promo_fim: '',
    imagem_url: '',
    unidade: 'UN',
    estoque_disponivel: '',
    em_destaque: false,
    visivel: true,
  });

  useEffect(() => {
    if (product) {
      setForm({
        codigo: product.codigo,
        descricao: product.descricao,
        descricao_curta: product.descricao_curta ?? '',
        categoria: product.categoria ?? '',
        departamento: product.departamento ?? '',
        marca: product.marca ?? '',
        preco_reais: ((product.preco_cents ?? 0) / 100).toFixed(2).replace('.', ','),
        preco_promo_reais: product.preco_promo_cents
          ? (product.preco_promo_cents / 100).toFixed(2).replace('.', ',')
          : '',
        promo_inicio: product.promo_inicio ? product.promo_inicio.slice(0, 10) : '',
        promo_fim: product.promo_fim ? product.promo_fim.slice(0, 10) : '',
        imagem_url: product.imagem_url ?? '',
        unidade: product.unidade ?? 'UN',
        estoque_disponivel: product.estoque_disponivel != null ? String(product.estoque_disponivel) : '',
        em_destaque: !!product.em_destaque,
        visivel: product.visivel !== false,
      });
    }
  }, [product]);

  function update<K extends keyof typeof form>(k: K, v: typeof form[K]) {
    setForm(prev => ({ ...prev, [k]: v }));
  }

  async function onPickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      update('imagem_url', await uploadMedia(file, 'product'));
      toast.success('Imagem enviada');
    } catch (err: any) {
      toast.error(err?.message ?? 'Falha ao enviar imagem');
    } finally {
      setUploading(false);
    }
  }

  function brToCents(s: string): number {
    if (!s) return 0;
    const cleaned = s.replace(/\./g, '').replace(',', '.');
    const n = parseFloat(cleaned);
    return isNaN(n) ? 0 : Math.round(n * 100);
  }

  const mut = useMutation({
    mutationFn: async () => {
      const payload = {
        codigo: form.codigo.trim(),
        descricao: form.descricao.trim(),
        descricao_curta: form.descricao_curta || null,
        marca: form.marca || null,
        categoria: form.categoria || null,
        departamento: form.departamento || null,
        preco_cents: brToCents(form.preco_reais),
        preco_promo_cents: form.preco_promo_reais ? brToCents(form.preco_promo_reais) : null,
        promo_inicio: form.promo_inicio || null,
        promo_fim: form.promo_fim || null,
        imagem_url: form.imagem_url || null,
        unidade: form.unidade || null,
        estoque_disponivel: form.estoque_disponivel ? parseFloat(form.estoque_disponivel) : null,
        em_destaque: form.em_destaque,
        visivel: form.visivel,
      };
      return isEdit
        ? apiClient.updateProduct(form.codigo, payload)
        : apiClient.createProduct(payload);
    },
    onSuccess: () => {
      toast.success(isEdit ? 'Produto atualizado' : 'Produto criado');
      qc.invalidateQueries({ queryKey: ['products'] });
      onClose();
    },
    onError: (err: any) => {
      const code = err?.response?.data?.error;
      if (code === 'codigo_ja_existe') toast.error('Já existe um produto com este código');
      else if (code === 'codigo_e_descricao_obrigatorios') toast.error('Código e descrição são obrigatórios');
      else toast.error('Erro ao salvar produto');
    },
  });

  const delMut = useMutation({
    mutationFn: () => apiClient.deleteProduct(form.codigo),
    onSuccess: () => {
      toast.success('Produto removido');
      qc.invalidateQueries({ queryKey: ['products'] });
      onClose();
    },
  });

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.codigo.trim() || !form.descricao.trim()) {
      toast.error('Código e descrição são obrigatórios');
      return;
    }
    if (brToCents(form.preco_reais) <= 0) {
      toast.error('Preço deve ser maior que zero');
      return;
    }
    if (form.preco_promo_reais && brToCents(form.preco_promo_reais) >= brToCents(form.preco_reais)) {
      toast.error('Preço promocional deve ser menor que o preço normal');
      return;
    }
    mut.mutate();
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4 overflow-y-auto">
      <div className="bg-card rounded-lg shadow-2xl w-full max-w-2xl my-8">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-xl font-bold">
            {isEdit ? 'Editar produto' : 'Novo produto'}
          </h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground p-1">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={onSubmit} className="p-6 space-y-5 max-h-[70vh] overflow-y-auto">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium">Código *</label>
              <Input
                required
                disabled={isEdit}
                value={form.codigo}
                onChange={e => update('codigo', e.target.value)}
                placeholder="EAN ou SKU"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Unidade</label>
              <select
                value={form.unidade}
                onChange={e => update('unidade', e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              >
                {COMMON_UNIDADES.map(u => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="text-sm font-medium">Descrição *</label>
            <Input
              required
              value={form.descricao}
              onChange={e => update('descricao', e.target.value)}
              placeholder="Ex: Picanha Premium"
            />
          </div>

          <div>
            <label className="text-sm font-medium">Descrição curta (opcional, para TVs)</label>
            <Input
              value={form.descricao_curta}
              onChange={e => update('descricao_curta', e.target.value)}
              placeholder="Para mostrar em telas com pouco espaço"
              maxLength={40}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium">Categoria</label>
              <input
                list="categorias-list"
                value={form.categoria}
                onChange={e => update('categoria', e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                placeholder="Ex: Carnes Bovinas"
              />
              <datalist id="categorias-list">
                {managedCategories.map(c => <option key={c.nome} value={c.nome} />)}
              </datalist>
            </div>
            <div>
              <label className="text-sm font-medium">Departamento</label>
              <input
                list="departamentos-list"
                value={form.departamento}
                onChange={e => update('departamento', e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                placeholder="Ex: Açougue"
              />
              <datalist id="departamentos-list">
                {managedDepartments.map(d => <option key={d.nome} value={d.nome} />)}
              </datalist>
            </div>
          </div>

          <div>
            <label className="text-sm font-medium">Marca</label>
            <Input
              value={form.marca}
              onChange={e => update('marca', e.target.value)}
              placeholder="Opcional"
            />
          </div>

          <div className="rounded-md border p-4 space-y-3 bg-muted/30">
            <div className="text-sm font-semibold">Preço</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground">Preço normal (R$) *</label>
                <Input
                  required
                  inputMode="decimal"
                  value={form.preco_reais}
                  onChange={e => update('preco_reais', e.target.value)}
                  placeholder="89,90"
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Preço promoção (R$)</label>
                <Input
                  inputMode="decimal"
                  value={form.preco_promo_reais}
                  onChange={e => update('preco_promo_reais', e.target.value)}
                  placeholder="74,90 (deixe vazio se não houver)"
                />
              </div>
            </div>
            {form.preco_promo_reais && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-muted-foreground">Promoção começa em</label>
                  <Input type="date" value={form.promo_inicio} onChange={e => update('promo_inicio', e.target.value)} />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Promoção termina em</label>
                  <Input type="date" value={form.promo_fim} onChange={e => update('promo_fim', e.target.value)} />
                </div>
              </div>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-sm font-medium">URL da imagem</label>
              <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-md border border-input bg-background hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                title="Enviar imagem do computador (PNG, JPG ou WebP até 5 MB)"
              >
                {uploading
                  ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  : <Upload className="h-3.5 w-3.5" />}
                {uploading ? 'Enviando…' : 'Enviar imagem'}
              </button>
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="hidden"
                onChange={onPickFile}
              />
              <button
                type="button"
                onClick={() => setImageSearchOpen(true)}
                disabled={!form.descricao.trim()}
                className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-md bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white shadow-sm hover:shadow-md hover:from-violet-600 hover:to-fuchsia-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                title={form.descricao.trim() ? 'Buscar imagens automaticamente' : 'Preencha a descrição primeiro'}
              >
                <Sparkles className="h-3.5 w-3.5" />
                Buscar imagens
              </button>
              </div>
            </div>
            <Input
              value={form.imagem_url}
              onChange={e => update('imagem_url', e.target.value)}
              placeholder="https://..."
            />
            {form.imagem_url && (
              <img src={form.imagem_url} alt="" className="mt-2 max-h-32 object-contain border rounded" />
            )}
          </div>

          <div>
            <label className="text-sm font-medium">Estoque disponível</label>
            <Input
              type="number"
              step="0.01"
              value={form.estoque_disponivel}
              onChange={e => update('estoque_disponivel', e.target.value)}
              placeholder="Opcional, informativo"
            />
          </div>

          <div className="space-y-2 pt-2">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.em_destaque} onChange={e => update('em_destaque', e.target.checked)} />
              <span>Produto em destaque (aparece primeiro nas TVs)</span>
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.visivel} onChange={e => update('visivel', e.target.checked)} />
              <span>Visível nas TVs</span>
            </label>
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t bg-muted/20">
          <div>
            {isEdit && (
              <Button
                variant="ghost"
                className="text-red-600"
                onClick={() => confirm(`Excluir produto "${form.descricao}"?`) && delMut.mutate()}
                loading={delMut.isPending}
              >
                <Trash2 className="h-4 w-4" />
                Excluir
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancelar</Button>
            <Button onClick={onSubmit} variant="gradient" loading={mut.isPending}>
              <SaveIcon className="h-4 w-4" />
              {isEdit ? 'Salvar' : 'Criar produto'}
            </Button>
          </div>
        </div>
      </div>

      {imageSearchOpen && (
        <ImageSearchModal
          initialQuery={[form.descricao, form.marca].filter(Boolean).join(' ').trim()}
          onSelect={(url) => {
            update('imagem_url', url);
            setImageSearchOpen(false);
            toast.success('Imagem selecionada');
          }}
          onClose={() => setImageSearchOpen(false)}
        />
      )}
    </div>
  );
}
